import { saveBet } from '@/api'
import { GameResult } from '@/datamodel/game-result'
import { getMemberSideBetAmounts, GoodRoadTableEntry } from './utils'
import AppSvg from '@/components/app-svg.vue'
import GoodRoadCardResult from './good-road-card-result.vue'
import RoadMapView from '@/components/roadmap/index.vue'
import { Options, Vue } from 'vue-class-component'
import { useI18n } from 'vue-i18n'
import { Prop, Watch } from 'vue-property-decorator'
import { MemberLimit, ResultMemberList, Table } from 'vue'
import { Action, Getter } from 'vuex-class'
import { BetError, EventBuss, SOCKET_MESSAGE } from '@/utils/constants'
// import audioPlayer, { SOUNDKEY } from '@/utils/sounds'

type BetMessageType = 'info' | 'success' | 'error' | 'win' | 'loss'
type BetSide = 'player' | 'banker'

@Options({
    components: {
        AppSvg,
        GoodRoadCardResult,
        RoadMapView
    }
})
export class GoodRoadItemMixin extends Vue {
    iN = useI18n().t

    deskResult = ''
    redTemp = 0
    blueTemp = 0
    redConfirmed = 0
    blueConfirmed = 0
    selectedBetArea: BetSide | null = null
    hasPendingRequest = false
    showMessage = false
    message = ''
    messageType: BetMessageType = 'info'
    messageTimer: number | boolean = false
    lastSettledGameNo = ''
    showCardResult = false
    currentGameResult: GameResult | undefined
    cardResultTimer: number | boolean = false
    lastCardResultGameNo = ''
    hasBetOnTable = false

    emitter!: Vue['emitter']
    $emit!: Vue['$emit']

    @Prop() entry!: GoodRoadTableEntry
    @Prop({ default: 0 }) chipValue!: number
    @Prop({ default: false }) pinned!: boolean

    @Getter('betCoin') betCoin!: number
    @Getter('token') token!: string
    @Getter('memberId') memberId!: number
    @Getter('deskLimit') deskLimit!: MemberLimit

    @Action('setBetCoin') setBetCoin!: (coin: number) => void

    beforeMount(): void {
        this.emitter.on(EventBuss.SOCKET_MESSAGE, this.handleSocketMessage)
    }

    beforeUnmount(): void {
        this.clearMessageTimer()
        this.hideCardResult()
        this.emitter.off(EventBuss.SOCKET_MESSAGE, this.handleSocketMessage)
    }

    enterDesk(): void {
        this.$emit('enter-desk')
    }

    get mdata() {
        return this.entry.table
    }

    get betTime(): number {
        return this.mdata.expand?.daoJiShiBetTime ?? -1
    }

    get goodRoadName(): string {
        return this.entry.goodRoadName
    }

    onTogglePin(): void {
        this.$emit('toggle-pin')
    }

    get title(): string {
        if (this.mdata.gameTypeNo === 'G002') {
            return `百家乐${this.mdata.tableNo}号桌`
        }
        return `龙虎${this.mdata.tableNo}号桌`
    }

    get roundLabel(): string {
        // return ''
        return `局数${this.mdata.expand.shoeNo}-${this.mdata.expand.juNo}`
    }

    get timerClass(): string {
        if (this.betTime > 20) return 'green'
        if (this.betTime > 10) return 'orange'
        if (this.betTime <= -1) return ''
        return 'red'
    }

    get timerLabel(): string {
        if (this.betTime >= 0) return String(this.betTime)
        else return '结算中'
        // 结算中
        // return ''
    }

    get playerLabel(): string {
        return this.mdata.gameTypeNo === 'G002' ? '闲' : '虎'
    }

    get bankerLabel(): string {
        return this.mdata.gameTypeNo === 'G002' ? '庄' : '龙'
    }

    get playerBetRatio(): string {
        if (this.mdata.gameTypeNo === 'G002') {
            return '1:1'
        }
        return '1:1'
    }

    get bankerBetRatio(): string {
        if (this.mdata.gameTypeNo === 'G002') {
            return '1:0.95'
        }
        return '1:1'
    }

    get isDragonTiger(): boolean {
        return this.mdata.gameTypeNo === 'G003'
    }

    get allowBet(): boolean {
        const expand = this.mdata.expand
        if (!expand) return false
        if (expand.stopBet === 1) return false
        if (expand.daoJiShiBetTime === -1) return false
        return expand.daoJiShiBetTime > 0
    }

    get hasUnconfirmedBet(): boolean {
        return this.redTemp > 0 || this.blueTemp > 0
    }

    get confirmDisabled(): boolean {
        return !this.hasUnconfirmedBet || this.hasPendingRequest
    }

    get cancelDisabled(): boolean {
        return !this.hasUnconfirmedBet || this.hasPendingRequest
    }

    get playerTempAmount(): number {
        return this.blueTemp
    }

    get bankerTempAmount(): number {
        return this.redTemp
    }

    get confirmedBanker(): number {
        return this.redConfirmed
    }

    get confirmedPlayer(): number {
        return this.blueConfirmed
    }

    get playerBetAmount(): string {
        return String(this.blueConfirmed + this.blueTemp)
    }

    get bankerBetAmount(): string {
        return String(this.redConfirmed + this.redTemp)
    }

    get showPlayerBetInfo(): boolean {
        return this.blueConfirmed + this.blueTemp > 0
    }

    get showBankerBetInfo(): boolean {
        return this.redConfirmed + this.redTemp > 0
    }

    get tableDownLimit(): number {
        return this.deskLimit?.downLimit || 0
    }

    get tableUpLimit(): number {
        return this.deskLimit?.upLimit || 0
    }

    checkIfValidBet(tempBet: number): number {
        const total = this.redTemp + this.blueTemp + tempBet

        if (total <= this.betCoin) return tempBet

        this.popMessage(BetError.BALANCE_SHORT, 'error')
        const finalTemp = this.betCoin - (total - tempBet)
        return finalTemp > 0 ? finalTemp : 0
    }

    checkRedAndBlueLimit(): string {
        const redTotal = this.redTemp + this.redConfirmed
        const blueTotal = this.blueTemp + this.blueConfirmed

        if (
            (this.redTemp > 0 && redTotal < this.tableDownLimit) ||
            (this.blueTemp > 0 && blueTotal < this.tableDownLimit)
        ) {
            return BetError.BELOW_MINIMUM
        }

        if (Math.abs(redTotal - blueTotal) > this.tableUpLimit) {
            return BetError.ABOVE_MAXIMUM
        }

        return BetError.BET_OK
    }

    placeBetSide(side: BetSide): void {
        if (this.hasPendingRequest || !this.allowBet) return

        if (this.chipValue <= 0) {
            this.popMessage(BetError.NO_CHIPS_SELECTED, 'error')
            return
        }

        const toBetAmount = Math.floor(this.checkIfValidBet(this.chipValue))
        if (toBetAmount <= 0) return

        if (side === 'banker') {
            const redTotal = this.redTemp + this.redConfirmed + toBetAmount
            const blueTotal = this.blueTemp + this.blueConfirmed

            if (Math.abs(redTotal - blueTotal) > this.tableUpLimit) {
                this.popMessage(BetError.ABOVE_MAXIMUM, 'error')
                return
            }

            this.redTemp += toBetAmount
            this.selectedBetArea = 'banker'
            return
        }

        const redTotal = this.redTemp + this.redConfirmed
        const blueTotal = this.blueTemp + this.blueConfirmed + toBetAmount

        if (Math.abs(redTotal - blueTotal) > this.tableUpLimit) {
            this.popMessage(BetError.ABOVE_MAXIMUM, 'error')
            return
        }

        this.blueTemp += toBetAmount
        this.selectedBetArea = 'player'
    }

    clearBetTemp(): void {
        this.redTemp = 0
        this.blueTemp = 0
        this.selectedBetArea = null
    }

    cancelSelection(): void {
        if (this.hasPendingRequest) return
        this.clearBetTemp()
    }

    confirmSelection(): void {
        if (!this.hasUnconfirmedBet || this.hasPendingRequest) return

        if (!this.allowBet) {
            this.popMessage(
                this.mdata.expand?.daoJiShiBetTime === -1
                    ? '结算中'
                    : this.iN('desk.nobet'),
                'error'
            )
            return
        }

        const limitError = this.checkRedAndBlueLimit()
        if (limitError !== BetError.BET_OK) {
            this.popMessage(limitError, 'error')
            return
        }

        this.sendBetRequest()
    }

    sendBetRequest(): void {
        this.hasPendingRequest = true

        const params: Record<string, number | string> = {
            token: this.token,
            memberId: this.memberId,
            shoeNo: this.mdata.expand.shoeNo,
            juNo: this.mdata.expand.juNo,
            gameNo: this.mdata.expand.gameNo,
            tableId: this.mdata.id
        }

        if (this.redTemp > 0) params.zhuang = this.redTemp
        if (this.blueTemp > 0) params.xian = this.blueTemp

        saveBet(params)
            .then(r => {
                this.hasPendingRequest = false
                if (r.data.status === 200) {
                    // audioPlayer.Play(SOUNDKEY.BET_SUCCESS)
                    const updatedTable = r.data.data.gameTable
                    const balance = updatedTable.expand?.amount
                    if (balance !== undefined && balance !== null) {
                        this.setBetCoin(balance)
                    }
                    this.hasBetOnTable = true
                    this.popMessage('下注成功', 'success')
                    this.clearBetTemp()
                    this.syncConfirmedBets(updatedTable)
                    this.$emit('on-bet-success', updatedTable)
                } else {
                    const description = r.data.description || ''
                    this.popMessage(
                        description.includes('余额') ||
                            description.toLowerCase().includes('balance')
                            ? BetError.BALANCE_SHORT
                            : description || BetError.UNKNOWN_ERROR,
                        'error'
                    )
                }
            })
            .catch(() => {
                this.hasPendingRequest = false
                this.popMessage(this.iN('prompt.networkerror'), 'error')
            })
    }

    syncConfirmedBets(table: Table = this.mdata): void {
        const { player, banker } = getMemberSideBetAmounts(table, this.memberId)
        this.blueConfirmed = player
        this.redConfirmed = banker
        this.hasBetOnTable = player + banker > 0
    }

    handleSocketMessage(_message: { type?: string; gameTable?: Table }): void {
        const type = _message.type?.toUpperCase()
        const table = _message.gameTable
        if (!table || table.id !== this.mdata.id) return

        if (type === SOCKET_MESSAGE.END_GAME) {
            this.showCardResultFromTable(table)
            this.handleGameResult(table)
            return
        }

        if (type === SOCKET_MESSAGE.NEW_GAME) {
            this.clearBetTemp()
            this.hideCardResult()
            return
        }

        if (
            type === SOCKET_MESSAGE.STOP_BET ||
            type === SOCKET_MESSAGE.STOP_GAME
        ) {
            this.clearBetTemp()
        }
    }

    showCardResultFromTable(table: Table): void {
        if (!table.expand?.bankerOne) return

        const gameNo = table.expand.gameNo || ''
        if (!gameNo || gameNo === this.lastCardResultGameNo) return

        const gameResult = new GameResult(table)
        if (!gameResult.hasCardToDisplay) return

        this.lastCardResultGameNo = gameNo
        this.currentGameResult = gameResult
        this.showCardResult = true

        this.clearCardResultTimer()
        this.cardResultTimer = window.setTimeout(() => {
            this.hideCardResult()
        }, 4000)
    }

    hideCardResult(): void {
        this.showCardResult = false
        this.currentGameResult = undefined
        this.clearCardResultTimer()
    }

    clearCardResultTimer(): void {
        if (typeof this.cardResultTimer === 'number') {
            clearTimeout(this.cardResultTimer)
            this.cardResultTimer = false
        }
    }

    handleGameResult(table: Table): void {
        const gameNo = table.expand?.gameNo || ''
        if (!gameNo || gameNo === this.lastSettledGameNo) return

        const memberResults = table.expand?.winOrLoseList as
            | ResultMemberList[]
            | undefined
        if (!memberResults?.length || !this.hasBetOnTable) return

        const mine = memberResults.find(r => r.memberId === this.memberId)
        if (!mine) return

        this.lastSettledGameNo = gameNo
        this.hasBetOnTable = false

        if (mine.winResult > 0) {
            this.popMessage(`+${mine.winResult}`, 'win')
        } else if (mine.winResult < 0) {
            this.popMessage(String(mine.winResult), 'loss')
        } else {
            this.popMessage('和局', 'info')
        }
    }

    popMessage(msg: string, type: BetMessageType = 'info'): void {
        this.clearMessageTimer()
        this.message = msg
        this.messageType = type
        this.showMessage = true
        this.messageTimer = window.setTimeout(() => {
            this.showMessage = false
            this.message = ''
            this.messageTimer = false
        }, 2000)
    }

    clearMessageTimer(): void {
        if (typeof this.messageTimer === 'number') {
            clearTimeout(this.messageTimer)
            this.messageTimer = false
        }
    }

    @Watch('allowBet')
    onAllowBetChange(allowed: boolean): void {
        if (!allowed) this.clearBetTemp()
    }

    @Watch('mdata', { immediate: true, deep: true })
    onDataChange(value: Table): void {
        if (!value) return

        this.deskResult = value.expand?.resultList?.join('#') || ''

        if (value.expand.stopBet === 1) {
            this.clearBetTemp()
        }

        this.syncConfirmedBets(value)

        if (value.expand.daoJiShiBetTime === -1) {
            this.showCardResultFromTable(value)
            this.handleGameResult(value)
        }
    }
}
